import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useSEO } from '@/hooks/useSEO';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { CheckCircle2, XCircle, Loader2, Shield, Lock, LogOut, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const SecureAccount = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [verifying, setVerifying] = useState(true);
  const [valid, setValid] = useState(false);
  const [step, setStep] = useState(1);
  const [completing, setCompleting] = useState(false);

  useSEO({
    title: 'Secure Your Account - ChessHatch',
    description: 'Emergency account security actions',
  });

  useEffect(() => {
    const verifyToken = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setValid(false);
        setVerifying(false);
        return;
      }

      try {
        const { data: tokenData, error } = await supabase
          .from('security_alert_tokens' as any)
          .select('*')
          .eq('secure_token', token)
          .single();

        if (error || !tokenData) {
          setValid(false);
          setVerifying(false);
          return;
        }

        if ((tokenData as any).used) {
          toast.error('This security link has already been used');
          setValid(false);
          setVerifying(false);
          return;
        }

        if (new Date((tokenData as any).expires_at) < new Date()) {
          toast.error('This security link has expired');
          setValid(false);
          setVerifying(false);
          return;
        }

        setValid(true);
        setVerifying(false);
      } catch (error) {
        console.error('Error verifying token:', error);
        setValid(false);
        setVerifying(false);
      }
    };

    verifyToken();
  }, [searchParams]);

  const handleCompleteStep = async () => {
    if (step < 4) {
      setStep(step + 1);
    } else {
      // Mark token as used
      const token = searchParams.get('token');
      if (token) {
        await supabase
          .from('security_alert_tokens' as any)
          .update({ used: true, used_at: new Date().toISOString() } as any)
          .eq('secure_token', token);
      }

      // Complete security process
      setCompleting(true);
      toast.success('Security actions completed');
      
      // Sign out and redirect to auth
      setTimeout(async () => {
        await signOut();
        navigate('/auth?message=password_reset_required');
      }, 2000);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <Loader2 className="w-12 h-12 mx-auto mb-4 text-primary animate-spin" />
            <p className="text-muted-foreground">Verifying security link...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!valid) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-10 h-10 text-red-600 dark:text-red-400" />
            </div>
            <CardTitle>Invalid Security Link</CardTitle>
            <CardDescription>
              This security link is invalid, expired, or has already been used.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/security/dashboard')} className="w-full">
              Go to Security Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalSteps = 4;
  const progress = (step / totalSteps) * 100;

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="container max-w-2xl mx-auto space-y-6">
        {/* Header Alert */}
        <Alert className="border-destructive bg-destructive/10">
          <AlertTriangle className="h-5 w-5 text-destructive" />
          <AlertDescription className="text-destructive font-semibold">
            Security Alert: We detected suspicious activity on your account. Follow these steps to secure it.
          </AlertDescription>
        </Alert>

        {/* Progress */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between mb-2">
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-6 h-6" />
                Securing Your Account
              </CardTitle>
              <span className="text-sm text-muted-foreground">
                Step {step} of {totalSteps}
              </span>
            </div>
            <Progress value={progress} className="h-2" />
          </CardHeader>
        </Card>

        {/* Step Content */}
        <Card>
          <CardHeader>
            <CardTitle>
              {step === 1 && 'Review Suspicious Login'}
              {step === 2 && 'Change Your Password'}
              {step === 3 && 'Enable Two-Factor Authentication'}
              {step === 4 && 'Review Active Sessions'}
            </CardTitle>
            <CardDescription>
              {step === 1 && 'Confirm this login was not authorized by you'}
              {step === 2 && 'Create a strong, unique password'}
              {step === 3 && 'Add an extra layer of security'}
              {step === 4 && 'Log out all other devices'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {step === 1 && (
              <div className="space-y-4">
                <Alert className="bg-destructive/10 border-destructive">
                  <AlertDescription>
                    If you recognize this login, you can dismiss this alert. Otherwise, continue with the security steps.
                  </AlertDescription>
                </Alert>
                <div className="flex gap-2">
                  <Button onClick={() => navigate('/security/dashboard')} variant="outline" className="flex-1">
                    This Was Me
                  </Button>
                  <Button onClick={handleCompleteStep} className="flex-1">
                    Continue Securing Account
                  </Button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                  <h4 className="font-semibold">Password Requirements:</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground">
                    <li>• At least 12 characters long</li>
                    <li>• Mix of uppercase and lowercase letters</li>
                    <li>• Include numbers and special characters</li>
                    <li>• Don't reuse old passwords</li>
                  </ul>
                </div>
                <Alert>
                  <Lock className="h-4 w-4" />
                  <AlertDescription>
                    You'll be logged out after this process. You'll need to sign in with your new password.
                  </AlertDescription>
                </Alert>
                <Button onClick={handleCompleteStep} className="w-full">
                  I Understand - Continue
                </Button>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                <div className="bg-primary/5 border border-primary/20 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">Why Enable 2FA?</h4>
                  <p className="text-sm text-muted-foreground">
                    Two-factor authentication adds an extra layer of security by requiring both your password 
                    and a verification code from your phone to log in.
                  </p>
                </div>
                <Alert>
                  <CheckCircle2 className="h-4 w-4" />
                  <AlertDescription>
                    You can set up 2FA in your Security Settings after completing this process.
                  </AlertDescription>
                </Alert>
                <Button onClick={handleCompleteStep} className="w-full">
                  Continue
                </Button>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-4">
                <Alert className="bg-destructive/10 border-destructive">
                  <LogOut className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Final Step:</strong> We'll log out all your active sessions on other devices. 
                    You'll need to log in again on each device.
                  </AlertDescription>
                </Alert>
                <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                  <h4 className="font-semibold">What happens next:</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground">
                    <li>✓ All other sessions will be terminated</li>
                    <li>✓ You'll be signed out of this device</li>
                    <li>✓ You'll need to reset your password</li>
                    <li>✓ Enable 2FA in Security Settings (recommended)</li>
                  </ul>
                </div>
                <Button 
                  onClick={handleCompleteStep} 
                  className="w-full"
                  disabled={completing}
                >
                  {completing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Securing Account...
                    </>
                  ) : (
                    <>
                      <Shield className="w-4 h-4 mr-2" />
                      Complete Security Process
                    </>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Help */}
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground text-center">
              Need help? Contact our security team at{' '}
              <a href="mailto:security@chesshatch.com" className="text-primary hover:underline">
                security@chesshatch.com
              </a>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SecureAccount;
